import { sanitizeRut } from '../../../../utils/sanitizeRut'

function isValidRut(rut) {
  if (!rut || typeof rut !== 'string') return false
  const cleanRut = rut.replace(/\./g, '').toUpperCase()
  const [number, dv] = cleanRut.split('-')
  if (!number || !dv) return false

  let s = 0
  let m = 2
  for (let i = number.length - 1; i >= 0; i--) {
    s += number[i] * m
    m = m === 7 ? 2 : m + 1
  }
  const expectedDv = 11 - (s % 11)
  const dvStr = expectedDv === 11 ? '0' : expectedDv === 10 ? 'K' : String(expectedDv)
  return dvStr === dv
}

function isBlankCell(value) {
  return value === undefined || value === null || (typeof value === 'string' && value.trim() === '')
}

export const mapFrecuencia = val => {
  if (!val) return null
  const lower = String(val).toLowerCase().trim()
  if (lower.includes('diari')) return 'daily'
  if (lower.includes('semanal')) return 'weekly'
  if (lower.includes('mensual')) return 'monthly'
  if (lower.includes('trimestral')) return 'quarterly'
  if (lower.includes('semestral')) return 'biannual'
  if (lower.includes('anual')) return 'yearly'
  return null
}

export const mapTipoFinalizacion = val => {
  if (!val) return 'never' // Default 'never'
  const lower = String(val).toLowerCase().trim()
  if (lower.includes('nunca')) return 'never'
  if (lower.includes('fecha')) return 'on_date'
  if (lower.includes('repeticion') || lower.includes('emisiones')) return 'after_occurrences'
  return 'never'
}

export const mapTipoDocumento = val => {
  if (!val) return ''
  const lower = String(val).toLowerCase().trim()
  if (lower === '33' || lower === 'factura electrónica' || lower === 'factura electronica') return '33'
  if (lower === '34' || lower === 'factura exenta') return '34'
  if (lower === '39') return '39'
  if (lower === '46') return '46'
  if (lower === '110') return '110'
  return String(val).trim()
}

export const mapDiaSemana = val => {
  if (val === undefined || val === null) return null
  const lower = String(val).toLowerCase().trim()
  const days = {
    'lunes': 1, '1': 1,
    'martes': 2, '2': 2,
    'miércoles': 3, 'miercoles': 3, '3': 3,
    'jueves': 4, '4': 4,
    'viernes': 5, '5': 5,
    'sábado': 6, 'sabado': 6, '6': 6,
    'domingo': 7, '7': 7
  }
  return days[lower] || null
}

export const excelRowsToScheduledDocs = rows => {
  if (!Array.isArray(rows) || rows.length === 0) return { documents: [], report: { loaded: 0, failed: 0, errorDetails: [] } }

  const documents = []
  const errorDetails = []
  const today = new Date().toISOString().split('T')[0]

  // Campos relevantes del documento: si TODOS están vacíos, la fila se ignora.
  // Esto evita validar filas fantasma causadas por fórmulas de Excel.
  const RELEVANT_FIELDS = ['rut_receptor', 'tipo_documento', 'frecuencia', 'nombre_producto', 'cantidad', 'precio_unitario']

  const isEmptyRow = row => {
    return RELEVANT_FIELDS.every(field => {
      const val = row[field]
      return val === undefined || val === null || String(val).trim() === ''
    })
  }

  rows.forEach((row, index) => {
    const rowNumber = index + 2 // Asumiendo que la fila 1 son encabezados

    // Omitir filas completamente vacías (sin datos relevantes)
    if (isEmptyRow(row)) return

    const errors = []

    const rut = sanitizeRut(row.rut_receptor || '')
    const tipoStr = row.tipo_documento ? String(row.tipo_documento).trim() : ''
    const tipoMapped = mapTipoDocumento(tipoStr)

    // 1. rut_receptor
    if (!row.rut_receptor) {
      errors.push(`Fila ${rowNumber}: Falta el RUT del receptor`)
    } else if (!isValidRut(rut)) {
      errors.push(`Fila ${rowNumber}: RUT inválido (${rut})`)
    }

    // 2. tipo_documento
    if (!tipoStr) {
      errors.push(`Fila ${rowNumber}: Falta el tipo de documento`)
    } else if (!['33', '34', '39', '46', '110'].includes(tipoMapped)) {
      errors.push(`Fila ${rowNumber}: Tipo de documento no permitido (${tipoStr}). Solo se admiten Factura Electrónica o Exenta (33, 34, etc)`)
    }

    // 3. frecuencia
    const freqRaw = row.frecuencia ? String(row.frecuencia).trim() : ''
    const freqMapped = mapFrecuencia(freqRaw)
    if (!freqRaw) {
      errors.push(`Fila ${rowNumber}: Falta la frecuencia de emisión`)
    } else if (!freqMapped) {
      errors.push(`Fila ${rowNumber}: Frecuencia inválida (${freqRaw})`)
    }

    // 4. fecha_inicio (Opcional)
    let fechaInicioParsed = null
    if (row.fecha_inicio) {
      let normalizedDate = row.fecha_inicio
      if (row.fecha_inicio instanceof Date) {
        normalizedDate = row.fecha_inicio.toISOString().split('T')[0]
      } else if (typeof row.fecha_inicio === 'number') {
        const date = new Date(Math.round((row.fecha_inicio - 25569) * 86400 * 1000))
        normalizedDate = date.toISOString().split('T')[0]
      }

      if (normalizedDate < today) {
        errors.push(`Fila ${rowNumber}: La fecha de inicio (${normalizedDate}) debe ser la fecha actual o futura`)
      } else {
        fechaInicioParsed = normalizedDate
      }
    }

    // 5. dia_del_mes
    const reqDiaMes = ['monthly', 'quarterly', 'biannual', 'yearly'].includes(freqMapped)
    let diaDelMesParsed = 15 // Default backend fallback
    if (reqDiaMes) {
      const valDiaMes = row.dia_del_mes !== undefined && row.dia_del_mes !== null ? String(row.dia_del_mes).trim() : ''
      if (!valDiaMes) {
        errors.push(`Fila ${rowNumber}: El día del mes es obligatorio para la frecuencia "${freqRaw}"`)
      } else {
        diaDelMesParsed = Number(valDiaMes)
        if (isNaN(diaDelMesParsed) || diaDelMesParsed < 1 || diaDelMesParsed > 31) {
          errors.push(`Fila ${rowNumber}: Día del mes inválido (${valDiaMes}). Debe ser un número entre 1 y 31`)
        }
      }
    }

    // 6. dia_de_semana
    const reqDiaSemana = freqMapped === 'weekly'
    let diaDeSemanaParsed = 1 // Default fallback
    if (reqDiaSemana) {
      const valDiaSemana = row.dia_de_semana !== undefined && row.dia_de_semana !== null ? String(row.dia_de_semana).trim() : ''
      if (!valDiaSemana) {
        errors.push(`Fila ${rowNumber}: El día de la semana es obligatorio para la frecuencia "${freqRaw}"`)
      } else {
        const diaMapped = mapDiaSemana(valDiaSemana)
        if (diaMapped === null) {
          errors.push(`Fila ${rowNumber}: Día de la semana inválido (${valDiaSemana}). Usa palabras como "lunes", "martes", etc.`)
        } else {
          diaDeSemanaParsed = diaMapped
        }
      }
    }

    // 7, 8, 9. tipo_finalizacion, fecha_fin, max_repeticiones
    const tipoFinRaw = row.tipo_finalizacion ? String(row.tipo_finalizacion).trim() : ''
    const tipoFin = mapTipoFinalizacion(tipoFinRaw)
    let fechaFinParsed = null
    let maxEmisionesParsed = null

    if (tipoFin === 'on_date') {
      if (isBlankCell(row.fecha_fin)) {
        errors.push(`Fila ${rowNumber}: Falta la fecha_fin (requerida cuando tipo_finalizacion es "en_fecha_especifica")`)
      } else {
        let normalizedDate = row.fecha_fin
        if (row.fecha_fin instanceof Date) {
          normalizedDate = row.fecha_fin.toISOString().split('T')[0]
        } else if (typeof row.fecha_fin === 'number') {
          const date = new Date(Math.round((row.fecha_fin - 25569) * 86400 * 1000))
          normalizedDate = date.toISOString().split('T')[0]
        } else if (typeof row.fecha_fin === 'string') {
          normalizedDate = row.fecha_fin.trim()
        }
        fechaFinParsed = normalizedDate

        if (fechaInicioParsed && fechaFinParsed <= fechaInicioParsed) {
          errors.push(`Fila ${rowNumber}: La fecha de fin no puede ser menor o igual a la fecha de inicio`)
        }
      }
    } else if (tipoFin === 'after_occurrences') {
      if (isBlankCell(row.max_repeticiones)) {
        errors.push(`Fila ${rowNumber}: Falta max_repeticiones (requerido cuando tipo_finalizacion es "despues_de_repeticiones")`)
      } else {
        const normalizedMaxEmisiones = typeof row.max_repeticiones === 'string' ? row.max_repeticiones.trim() : row.max_repeticiones
        maxEmisionesParsed = Number(normalizedMaxEmisiones)
        if (isNaN(maxEmisionesParsed) || maxEmisionesParsed <= 0) {
          errors.push(`Fila ${rowNumber}: max_repeticiones debe ser un entero positivo`)
        }
      }
    }

    // 10. nombre_producto
    if (row.nombre_producto === undefined || row.nombre_producto === null || String(row.nombre_producto).trim() === '') {
      errors.push(`Fila ${rowNumber}: Falta el nombre del producto`)
    }

    // 12. cantidad
    let cantidadParsed = 1
    if (row.cantidad === undefined || row.cantidad === null || String(row.cantidad).trim() === '') {
      errors.push(`Fila ${rowNumber}: Falta la cantidad`)
    } else {
      cantidadParsed = Number(row.cantidad)
      if (isNaN(cantidadParsed) || cantidadParsed <= 0) {
        errors.push(`Fila ${rowNumber}: La cantidad debe ser un número mayor a 0`)
      }
    }

    // 13. precio_unitario vs Moneda
    const moneda = String(row.moneda || 'CLP').toUpperCase().trim()
    let precioParsed = 0
    
    if (moneda === 'CLP') {
      if (row.precio_unitario === undefined || row.precio_unitario === null || String(row.precio_unitario).trim() === '') {
        errors.push(`Fila ${rowNumber}: Falta el precio unitario (Obligatorio para moneda CLP)`)
      } else {
        precioParsed = Number(row.precio_unitario)
        if (isNaN(precioParsed)) {
          errors.push(`Fila ${rowNumber}: El precio unitario debe ser un número`)
        }
      }
    } else if (['UF', 'USD'].includes(moneda)) {
      if (row.precio_unitario !== undefined && row.precio_unitario !== null && String(row.precio_unitario).trim() !== '') {
        errors.push(`Fila ${rowNumber}: El precio unitario debe quedar VACÍO si la moneda es UF o USD`)
      }
    }

    if (errors.length > 0) {
      errorDetails.push({
        row: rowNumber,
        receiver: row.rut_receptor || 'Sin receptor',
        errors
      })
    } else {
      const doc = {
        rut_receptor: rut,
        tipo_documento: tipoMapped,
        prioridad: row.prioridad || 0,
        frecuencia: freqMapped,
        fecha_inicio: fechaInicioParsed,
        dia_del_mes: diaDelMesParsed,
        dia_de_semana: diaDeSemanaParsed,
        tipo_finalizacion: tipoFin,
        fecha_fin: fechaFinParsed,
        max_emisiones: maxEmisionesParsed,
        currency: moneda,
        dia_uf: row.dia_uf ? Number(row.dia_uf) : null,
        business_name: '',
        email: '',
        address: '',
        details: [
          {
            item_name: String(row.nombre_producto).trim(),
            item_description: row.descripcion_producto ? String(row.descripcion_producto).trim() : '',
            quantity: cantidadParsed,
            unit_price: precioParsed,
            discount_percent: 0
          }
        ]
      }
      documents.push(doc)
    }
  })

  return {
    documents,
    report: {
      processed: rows.length,
      loaded: documents.length,
      failed: errorDetails.length,
      errorDetails
    }
  }
}
